<template>
  <DetailViewShell :dv="dv">
    <template #pre-buttons>
      <router-link
        v-if="!isLatest && pw.latestVersionId"
        class="cq-hide-narrow"
        :to="{
          name: 'workflowVersionDetail',
          params: { modelType: 'default', id: pw.latestVersionId },
        }"
      >
        <BaseButton
          :icon="['fa-solid', 'fa-arrow-up-right-from-square']"
          size="small"
          variant="primary"
          :outlined="true"
          label="Go to latest version"
        />
      </router-link>
      <template v-if="!dv.isEditMode && !dv.isDeleted">
        <template v-if="pw.isRemoteWorkflow">
          <router-link
            class="cq-hide-narrow"
            :to="{
              name: 'workflowImport',
              params: { modelType: 'default' },
              query: { workflowId: pw.workflowId },
            }"
          >
            <BaseButton
              :icon="['fa-regular', 'fa-plus']"
              size="small"
              variant="primary"
              :outlined="true"
              label="New Version"
            />
          </router-link>
        </template>
        <template
          v-else-if="
            isLatest && !pw.isRemoteWorkflow && !pw.importedFromROCrate
          "
        >
          <BaseButton
            class="cq-hide-narrow"
            :tooltip="
              pw.canAddVersion
                ? 'Create a new version'
                : 'The latest version must be released before a new one can be created.'
            "
            label="New Version"
            :icon="['fa-regular', 'fa-plus']"
            size="small"
            variant="primary"
            :outlined="true"
            :loading="pw.isCreatingWorkflowVersion"
            :disabled="!pw.canAddVersion || pw.isCreatingWorkflowVersion"
            @click="pw.createWorkflowVersion"
          />
        </template>
        <template v-if="pw.importedFromROCrate">
          <router-link
            class="cq-hide-narrow"
            :to="{
              name: 'workflowImportROCrate',
              params: { modelType: 'default' },
              query: { workflow_id: pw.workflowId },
            }"
          >
            <BaseButton
              :icon="['fa-regular', 'fa-plus']"
              size="small"
              variant="primary"
              :outlined="true"
              label="New Version"
              tooltip="Import a new version from WorkflowHub"
            />
          </router-link>
        </template>
      </template>
      <DropdownButton
        v-if="isLatest && !isReleased && !dv.isEditMode && dv.canEdit"
        class="cq-hide-narrow"
        tooltip="Release this version"
        :icon="['fa-solid', 'fa-rocket']"
        size="small"
        variant="primary"
        :outlined="true"
        :loading="isReleasing"
        :disabled="isReleasing"
        label="Release"
        align="end"
        :show-arrow="false"
        content-class="dd-content--confirm"
      >
        <div class="dd-confirm-body">
          <p class="mb-0">
            Once released, adding or removing files will no longer be possible.
            Other fields and file types remain editable.
          </p>
          <button
            class="button is-primary is-small has-text-weight-bold dd-confirm-btn"
            :class="{ 'is-loading': isReleasing }"
            :disabled="isReleasing"
            @click="handleRelease"
          >
            <span class="icon">
              <i class="fa-solid fa-rocket" />
            </span>
            <span>Release</span>
          </button>
        </div>
      </DropdownButton>
      <template v-if="!dv.isEditing">
        <BaseButton
          class="cq-hide-narrow"
          :tooltip="publishToolTip"
          :disabled="dv.isDeleted || isPublished || !isReleased"
          compact="auto"
          :icon="['fa-solid', 'fa-up-right-from-square']"
          size="small"
          variant="primary"
          :outlined="true"
          label="Publish"
          @click="togglePublishWorkflowHubModal"
        />
      </template>
    </template>

    <template #overflow-menu="{ isNarrow }">
      <div v-if="isNarrow" class="dd-section">
        <div class="dd-section-label">
          <span class="icon"><i class="fa-solid fa-ellipsis-h" /></span>
          Actions
        </div>
        <a
          v-if="!isLatest && pw.latestVersionId"
          class="dd-item"
          @click="
            $router.push({
              name: 'workflowVersionDetail',
              params: { modelType: 'default', id: pw.latestVersionId },
            })
          "
        >
          <span class="icon">
            <i class="fa-solid fa-arrow-up-right-from-square" />
          </span>
          Go to latest version
        </a>
        <template v-if="!dv.isEditMode && !dv.isDeleted">
          <a
            v-if="pw.isRemoteWorkflow"
            class="dd-item"
            @click="
              $router.push({
                name: 'workflowImport',
                params: { modelType: 'default' },
                query: { workflowId: pw.workflowId },
              })
            "
          >
            <span class="icon"><i class="fa-regular fa-plus" /></span>
            New Version
          </a>
          <a
            v-else-if="pw.importedFromROCrate"
            class="dd-item"
            @click="
              $router.push({
                name: 'workflowImportROCrate',
                params: { modelType: 'default' },
                query: { workflow_id: pw.workflowId },
              })
            "
          >
            <span class="icon"><i class="fa-regular fa-plus" /></span>
            New Version
          </a>
          <span
            v-else-if="isLatest"
            v-tooltip="
              !pw.canAddVersion
                ? 'The latest version must be released before a new one can be created.'
                : null
            "
          >
            <a
              class="dd-item"
              :class="{
                'is-disabled':
                  !pw.canAddVersion || pw.isCreatingWorkflowVersion,
              }"
              @click="pw.canAddVersion && pw.createWorkflowVersion()"
            >
              <span class="icon"><i class="fa-regular fa-plus" /></span>
              New Version
            </a>
          </span>
        </template>
        <a
          v-if="isLatest && !isReleased && !dv.isEditMode && dv.canEdit"
          class="dd-item"
          :class="{ 'is-disabled': isReleasing }"
          @click="!isReleasing && handleRelease()"
        >
          <span class="icon"><i class="fa-solid fa-rocket" /></span>
          Release
        </a>
        <span v-if="!dv.isEditing" v-tooltip="publishToolTip">
          <a
            class="dd-item"
            :class="{
              'is-disabled': dv.isDeleted || isPublished || !isReleased,
            }"
            @click="
              !(dv.isDeleted || isPublished || !isReleased) &&
              togglePublishWorkflowHubModal()
            "
          >
            <span class="icon">
              <i class="fa-solid fa-up-right-from-square" />
            </span>
            Publish
          </a>
        </span>
      </div>
    </template>
    <template #default>
      <DetailViewLayout :dv="dv">
        <template #panel-header>
          <span class="icon">
            <i class="fa-solid fa-barcode" />
          </span>
          <span>Item Details</span>
        </template>
        <template v-if="!dv.isEditing" #panel-header-controls>
          <StatusWidget
            v-if="dv.recordStatus"
            :status="dv.recordStatus"
            :field-meta="dv.modelAttributes[dv.recordStatusKey]"
          />
          <a
            v-if="wfv.isPublished"
            class="tag is-small is-primary"
            :href="wfv.publishedUrl"
            target="_blank"
            rel="noopener"
          >
            Published
            <span class="icon is-small ml-1">
              <i class="fa-solid fa-arrow-up-right-from-square" />
            </span>
          </a>
          <span v-if="isReleased" class="tag is-small is-primary">
            Released
          </span>
          <span v-else class="tag is-small is-primary is-light">Ongoing</span>
          <span v-if="isLatest" class="tag is-small is-primary">Latest</span>
          <BadgeTag
            v-if="wfv.isRemoteWorkflow"
            icon="fa-brands fa-git-alt"
            label="Remote Workflow"
            color="primary"
            :href="wfv.remoteRepositoryUrl"
          />
        </template>
        <template #panel-content>
          <RecordFieldSections :dv="dv">
            <template #workflowruns>
              <router-link
                :to="{
                  name: 'list',
                  params: {
                    appName: 'workflows',
                    modelName: 'workflowruns',
                    modelType: 'default',
                  },
                  query: {
                    workflowversion: recordId,
                    ownership: 'all',
                  },
                }"
                class="has-text-link"
              >
                {{ workflowRunsCount }}
                {{ workflowRunsCount === 1 ? 'item' : 'items' }}
              </router-link>
            </template>
          </RecordFieldSections>
        </template>
        <template #sidebar-before-attachments>
          <RocrateSection
            v-if="!dv.isDeleted"
            app-name="workflows"
            model-name="workflowversions"
            :record-id="id"
            :is-imported="Boolean(record.imported_from_rocrate?.value)"
          />
        </template>
        <template #extra-panels>
          <DefaultPanel v-if="dv.isEditing" class="mt-0">
            <template #header>
              {{ isReleased ? 'Modify files' : 'Add files' }}
            </template>
            <template v-if="!isReleased" #toolbar>
              <FilePond
                :ref="wfv.filepondRef"
                name="file"
                class="dropzone p-0"
                :allow-drop="true"
                :allow-multiple="true"
                :max-parallel-uploads="1"
                :label-idle="wfv.filePondLabel"
                label-tap-to-undo="Done"
                :allow-process="true"
                item-insert-location="after"
                :allow-remove="false"
                :before-add-file="handleBeforeAddFile"
              />
            </template>
            <fieldset :disabled="wfv.isProcessingFile">
              <WorkflowFiles
                :workflow-files="wfv.workflowFiles"
                :is-edit-mode="dv.isEditing"
                :file-options="wfv.remoteFileOptions"
                :can-delete-files="wfv.canDeleteFiles"
                :can-restore-files="wfv.canDeleteFiles"
                :type-options="wfv.typeOptions"
                :is-remote-workflow="wfv.isRemoteWorkflow"
                @delete-file="wfv.handleDeleteFile"
                @restore-file="wfv.handleRestoreFile"
                @change-file="wfv.handleChangeFile"
                @change-file-type="wfv.handleChangeFile"
              />
              <button
                v-if="wfv.canAddRemoteFile"
                v-tooltip="
                  `Add a file from the repository to the list of workflow
                    files for this workflow version.`
                "
                class="button is-small is-primary is-outlined"
                :disabled="wfv.remoteFileOptions.length === 0"
                @click="wfv.handleAddNextFile()"
              >
                <span class="icon">
                  <i class="fa-solid fa-plus" />
                </span>
              </button>
            </fieldset>
          </DefaultPanel>
          <DefaultPanel v-if="!dv.isEditing">
            <template #header>
              Files included in this version
              <span class="tag is-small is-rounded is-primary">
                {{ wfv.workflowFilesCount }}
              </span>
            </template>
            <template v-if="wfv.workflowFilesCount > 0" #default>
              <ListComponent
                class="injected-list edge-to-edge"
                app-name="workflows"
                model-name="workflowfiles"
                model-type="default"
                vuex-prefix="stock-version-detail-files-"
                :is-compact="true"
                :show-add-controls="false"
                :show-global-search="true"
                :show-owner-controls="true"
                :can-save-settings="true"
                :init-context="{
                  key: 'workflowversion_id',
                  id: id,
                }"
                init-ownership="all"
                :column-names="[
                  'name',
                  'data_type',
                  'workflowversion',
                  'file_path',
                  'owner',
                  'owned_by',
                ]"
                :show-deleted="dv.isDeleted"
              >
                <template #more-headers-columns>
                  <th class="cell-shrink cell-centered" />
                </template>
                <template #more-data-columns="{ record: fileRecord }">
                  <td class="cell-shrink cell-centered">
                    <WorkflowFilePreview :file="fileRecord" />
                  </td>
                </template>
              </ListComponent>
            </template>
          </DefaultPanel>
        </template>
      </DetailViewLayout>
    </template>
    <template #modals>
      <PublishWorkflowHubModal
        v-if="isOpenPublishWorkflowHubModal"
        :is-publishing="isPublishing"
        @publish="handlePublishWorkflowHub"
        @close="togglePublishWorkflowHubModal"
      />
    </template>
  </DetailViewShell>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue'

import { del, get, patch, post, put } from '@/api'
import { useDetailView } from '@/composables/useDetailView'
import { useFilePond } from '@/composables/useFilePond.js'
import { useRemoteRepositoryFiles } from '@/composables/useRemoteRepositoryFiles'
import { useWorkflowRecord } from '@/composables/useWorkflowRecord'
import { WORKFLOW_DATATYPES } from '@/constants'
import { FilePond } from '@/libs/filepond'
import { useStore } from '@/store'
import { scrollToElement } from '@/utils'

import DefaultPanel from '@/components/DefaultPanel.vue'
import RecordFieldSections from '@/components/detail/shared/RecordFieldSections.vue'
import DetailViewShell from '@/components/DetailViewShell.vue'
import ListComponent from '@/components/listings/ListComponent.vue'
import PublishWorkflowHubModal from '@/components/PublishWorkflowHubModal.vue'
import RocrateSection from '@/components/RocrateSection.vue'
import StatusWidget from '@/components/StatusWidget.vue'
import WorkflowFiles from '@/components/widget/WorkflowFiles.vue'
import WorkflowFilePreview from '@/components/WorkflowFilePreview.vue'
import DetailViewLayout from '@/layouts/DetailViewLayout.vue'

export default {
  name: 'WorkflowVersionDetailView',
  components: {
    DefaultPanel,
    DetailViewLayout,
    DetailViewShell,
    FilePond,
    PublishWorkflowHubModal,
    RecordFieldSections,
    ListComponent,
    RocrateSection,
    StatusWidget,
    WorkflowFiles,
    WorkflowFilePreview,
  },
  props: {
    appName: { type: String, required: true },
    modelName: { type: String, required: true },
    modelType: { type: String, required: true },
    id: { type: String, required: true },
  },
  setup(props) {
    const store = useStore()

    const dv = useDetailView({
      appName: props.appName,
      modelName: props.modelName,
      modelType: props.modelType,
      id: props.id,
    })

    // --- Record-derived refs (via dv) ---
    const record = computed(() => dv.record || {})
    const recordId = computed(() => dv.recordId || '')

    // --- Derived from record ---
    const isLatest = computed(() => record.value?.is_latest?.value === true)
    const isReleased = computed(() => record.value?.commit_hash?.value !== '')
    const isPublished = computed(
      () => (record.value?.publishedworkflows?.value?.length || 0) > 0,
    )
    const publishToolTip = computed(() => {
      if (isPublished.value) return 'Already published'
      if (!isReleased.value) return 'This version needs to be released first'
      return 'Publish to WorkflowHub'
    })

    const workflowId = computed(() => record.value?.workflow?.value?.id ?? null)

    // --- Parent workflow data ---
    const parentWorkflow = useWorkflowRecord({ id: workflowId })

    // Reactive proxy for clean template access (pw.isRemoteWorkflow, pw.latestVersionId, etc.)
    const pw = reactive({
      workflowId: computed(() => workflowId.value),
      isRemoteWorkflow: computed(() => parentWorkflow.isRemoteWorkflow.value),
      importedFromROCrate: computed(
        () => parentWorkflow.importedFromROCrate.value,
      ),
      canAddVersion: computed(() => parentWorkflow.canAddVersion.value),
      createWorkflowVersion: parentWorkflow.createWorkflowVersion,
      isCreatingWorkflowVersion: computed(
        () => parentWorkflow.isCreatingWorkflowVersion.value,
      ),
      latestVersionId: computed(() => parentWorkflow.latestVersionId.value),
    })

    // --- Workflow version content logic ---
    const typeOptions = WORKFLOW_DATATYPES

    const versionPublishedUrl = computed(() =>
      record.value?.publishedworkflows?.value?.length > 0
        ? record.value?.publishedworkflows?.value[0]?.url
        : '',
    )
    const versionIsRemoteWorkflow = computed(
      () => parentWorkflow.isRemoteWorkflow.value,
    )
    const versionRemoteRepositoryUrl = computed(
      () => parentWorkflow.remoteRepositoryUrl?.value || '',
    )

    // --- FilePond ---
    const filepondUrl = computed(
      () => `${props.appName}/workflowversions/${recordId.value}/upload/`,
    )
    const filePondLabel = computed(
      () =>
        '<p>Drag & Drop your workflow files</p> <p>or ' +
        "<span class='filepond--label-action'>Browse</span> local files</p>",
    )

    const workflowRunsCount = computed(
      () => record.value?.workflowruns?.value?.length || 0,
    )

    const { filepondEl, filepondRef, isProcessingFile } = useFilePond(
      filepondUrl,
      {},
      { method: 'PUT' },
    )

    async function handleBeforeAddFile() {
      if (!dv.isEditing) {
        await dv.enterEdit()
        await new Promise((resolve) => setTimeout(resolve, 800))
      }
      scrollToElement(filepondEl.value.$el)
      return Promise.resolve(true)
    }

    // --- Workflow files data ---
    const workflowFilesData = ref([])
    const workflowFilesCount = computed(() => workflowFilesData.value?.length)

    const fetchWfFilesUrl = computed(() => {
      let url = 'workflows/workflowfiles/'
      const fetchParams = new URLSearchParams({
        workflowversion_id: props.id,
        page_size: 'max',
        format: 'json',
        response_format: 'flat',
      })
      const qParamsStr = fetchParams.toString()
      if (qParamsStr) url += `?${qParamsStr}`
      return url
    })

    async function loadWorkflowFilesData() {
      let wfFiles = []
      try {
        const response = await get(fetchWfFilesUrl.value)
        if (response.status === 200) wfFiles = response.data.results
      } catch (error) {
        console.error('loadWorkflowFilesData', error)
      }
      workflowFilesData.value = wfFiles
    }

    watch(recordId, (id) => id && loadWorkflowFilesData(), { immediate: true })

    // --- Workflow files state ---
    const canDeleteFiles = computed(() => {
      const deletePermission = (
        record.value.permissions?.value?.allowed || []
      ).includes('delete')
      return (
        deletePermission && (versionIsRemoteWorkflow.value || !isReleased.value)
      )
    })

    const workflowFiles = ref({})
    watch(workflowFilesData, (wfFilesData) => {
      const files = {}
      for (const wfFile of wfFilesData) {
        files[wfFile.id] = {
          ...wfFile,
          isDeleting: false,
          isDeleted: false,
          isRestoring: false,
          isRestored: false,
          isChangingType: false,
        }
      }
      workflowFiles.value = files
    })

    // --- Remote files ---
    const { remoteFiles } = useRemoteRepositoryFiles({
      workflowVersionId: computed(
        () => versionIsRemoteWorkflow.value && recordId.value,
      ),
    })

    const workflowFilesPaths = computed(() =>
      Object.values(workflowFiles.value).map((f) => f.file_path),
    )
    const remoteFileOptions = computed(() =>
      remoteFiles.value.filter(
        (f) => !workflowFilesPaths.value.includes(f.file_path),
      ),
    )

    const canAddRemoteFile = computed(() => {
      return (
        dv.isEditMode &&
        canDeleteFiles.value &&
        remoteFileOptions.value.length > 0
      )
    })

    // --- File handlers ---
    const isAddingFile = ref(false)
    async function handleAddFile(file) {
      isAddingFile.value = true
      try {
        const response = await post(
          `workflows/workflowfiles/?response_format=flat`,
          {
            data: {
              results: {
                workflowversion: { id: recordId.value },
                name: file.file_path,
                file_path: file.file_path,
                data_type: file.data_type,
              },
            },
          },
        )
        const wfFile = response.data.results
        workflowFiles.value[wfFile.id] = wfFile
      } catch (e) {
        console.error('Error adding file', e)
      } finally {
        isAddingFile.value = false
      }
    }

    function handleAddNextFile() {
      if (remoteFileOptions.value.length === 0) return
      const nextFile = remoteFileOptions.value?.[0]
      handleAddFile(nextFile)
    }

    async function handleChangeFile({ file }) {
      const { id, file_path, data_type } = file
      if (!id || (!file_path && !data_type)) return
      const workflowFile = workflowFiles.value[id]
      workflowFile.isChangingType = true
      try {
        await patch(`workflows/workflowfiles/${id}/`, {
          data: {
            results: {
              ...(file_path && { file_path: { value: file_path } }),
              ...(data_type && { data_type: { value: data_type } }),
            },
          },
        })
        if (file_path) workflowFile.file_path = file_path
        if (data_type) workflowFile.data_type = data_type
      } catch (e) {
        const message =
          e.response?.data?.message || 'Failed to update workflow file'
        store.commit('notify', { type: 'error', message })
      } finally {
        workflowFile.isChangingType = false
      }
    }

    function deleteFile(fileId, restoreFile = false) {
      let qParamsStr = ''
      if (restoreFile) {
        qParamsStr =
          '?' + new URLSearchParams({ restore_previous: true }).toString()
      }
      return del(`workflows/workflowfiles/${fileId}/${qParamsStr}`)
    }

    async function handleDeleteFile({ file, restorePrevious = false }) {
      const fileId = file.id
      if (restorePrevious) workflowFiles.value[fileId].isRestoring = true
      else workflowFiles.value[fileId].isDeleting = true
      try {
        const response = await deleteFile(fileId, restorePrevious)
        if (response.status === 204) {
          if (restorePrevious) workflowFiles.value[fileId].isRestored = true
          else workflowFiles.value[fileId].isDeleted = true
          loadWorkflowFilesData()
        }
      } catch (e) {
        const message = e.response?.data?.message || 'Failed to delete file'
        store.commit('notify', { type: 'error', message })
      } finally {
        workflowFiles.value[fileId].isRestoring = false
        workflowFiles.value[fileId].isDeleting = false
      }
    }

    function handleRestoreFile({ file }) {
      return handleDeleteFile({ file, restorePrevious: true })
    }

    watch(isProcessingFile, (isProcessing) => {
      if (!isProcessing) {
        loadWorkflowFilesData()
      }
    })

    // Reactive proxy for version-specific template access
    const wfv = reactive({
      isPublished,
      publishedUrl: versionPublishedUrl,
      isRemoteWorkflow: versionIsRemoteWorkflow,
      remoteRepositoryUrl: versionRemoteRepositoryUrl,
      // FilePond
      filepondRef,
      filePondLabel,
      isProcessingFile,
      // Workflow files
      workflowFiles,
      workflowFilesCount,
      canDeleteFiles,
      typeOptions,
      // Remote files
      remoteFileOptions,
      canAddRemoteFile,
      // File handlers
      handleAddNextFile,
      handleDeleteFile,
      handleRestoreFile,
      handleChangeFile,
    })

    // --- Release ---
    const isReleasing = ref(false)
    const handleRelease = async () => {
      const releaseUrl = `workflows/workflowversions/${recordId.value}/commit/`
      try {
        isReleasing.value = true
        const response = await put(releaseUrl)
        if (response.status === 200) {
          store.commit('notify', {
            type: 'success',
            message: response?.data?.message || 'Workflow version released',
            isDismissable: true,
          })
          dv.loadRecord()
          parentWorkflow.loadRecord()
        }
      } catch (error) {
        let errorMessage = error?.response?.data?.message
        if (errorMessage === 'No file changes to commit.')
          errorMessage += '. This version is the same as the previous version.'
        store.commit('notify', {
          type: 'error',
          message: errorMessage || 'Workflow version could not be released',
          isDismissable: true,
        })
      } finally {
        isReleasing.value = false
      }
    }

    // --- Publish to WorkflowHub ---
    const isPublishing = ref(false)
    const isOpenPublishWorkflowHubModal = ref(false)

    function togglePublishWorkflowHubModal() {
      isOpenPublishWorkflowHubModal.value = !isOpenPublishWorkflowHubModal.value
    }

    async function handlePublishWorkflowHub({ url, project }) {
      const publishUrl = `workflows/workflowversions/${props.id}/publish_to_workflowhub/`
      try {
        isPublishing.value = true
        const response = await put(publishUrl, { data: { project, url } })
        if ([200, 202].includes(response.status)) {
          store.commit('notify', {
            type: 'success',
            message: response?.data?.message || 'Workflow version published',
            isDismissable: true,
          })
          try {
            localStorage.setItem('labid.lastWorkflowHubUrl', url)
          } catch {
            // localStorage may be unavailable (private mode); ignore.
          }
          dv.loadRecord()
          togglePublishWorkflowHubModal()
        }
      } catch (error) {
        store.commit('notify', {
          type: 'error',
          message:
            error?.response?.data?.message ||
            'Workflow version could not be published',
          isDismissable: true,
        })
      } finally {
        isPublishing.value = false
      }
    }

    return {
      dv,
      pw,
      wfv,
      // record-derived
      record,
      recordId,
      // derived helpers
      isLatest,
      isReleased,
      isPublished,
      publishToolTip,
      // release
      isReleasing,
      handleRelease,
      // FilePond
      handleBeforeAddFile,
      // publish
      isPublishing,
      isOpenPublishWorkflowHubModal,
      togglePublishWorkflowHubModal,
      handlePublishWorkflowHub,
      // RO-Crate
      workflowRunsCount,
    }
  },
}
</script>
